import { useMemo } from "react";
import { useApp } from "@/contexts/AppContext";
import Topbar from "@/components/layout/Topbar";
import { Truck, AlertCircle, TrendingUp, CheckCircle2 } from "lucide-react";

const fmt = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : "0");
const pct = (n) => (Number.isFinite(n) ? n.toFixed(0) + "%" : "—");

function buildSupplierStats(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const s = r.SupplierName || "Unknown";
    if (!map.has(s)) {
      map.set(s, { SupplierName: s, skus: 0, inventoryValue: 0, reorderValue: 0, stockoutRiskSKUs: 0, otdSum: 0, otdCount: 0, fillSum: 0, fillCount: 0 });
    }
    const e = map.get(s);
    e.skus += 1;
    e.inventoryValue += r.InventoryValue || 0;
    e.reorderValue += r.ReorderValue || 0;
    if (r.StockoutRisk === "High" || r.StockoutRisk === "Medium") e.stockoutRiskSKUs += 1;
    if (r.SupplierOTD !== "" && r.SupplierOTD !== undefined && r.SupplierOTD !== null) {
      e.otdSum += Number(r.SupplierOTD); e.otdCount += 1;
    }
    if (r.SupplierFillRate !== "" && r.SupplierFillRate !== undefined && r.SupplierFillRate !== null) {
      e.fillSum += Number(r.SupplierFillRate); e.fillCount += 1;
    }
  });
  return Array.from(map.values()).map((e) => {
    const otd = e.otdCount ? e.otdSum / e.otdCount : null;
    const fill = e.fillCount ? e.fillSum / e.fillCount : null;
    let action = "Monitor";
    let tone = "good";
    if (e.stockoutRiskSKUs >= Math.max(3, e.skus * 0.3)) { action = "Review lead times / replenishment plan"; tone = "danger"; }
    else if (otd !== null && otd < 80) { action = "Supplier performance review"; tone = "warn"; }
    else if (e.reorderValue > 5000) { action = "Prioritize replenishment discussion"; tone = "info"; }
    return { ...e, otd, fill, action, tone };
  }).sort((a, b) => b.reorderValue - a.reorderValue);
}

const TONE_CLASS = {
  good: "text-emerald-700 bg-emerald-50 border-emerald-200",
  warn: "text-amber-700 bg-amber-50 border-amber-200",
  danger: "text-red-700 bg-red-50 border-red-200",
  info: "text-sky-700 bg-sky-50 border-sky-200",
};

const TONE_ICON = { good: CheckCircle2, warn: TrendingUp, danger: AlertCircle, info: Truck };

export default function SupplierInsights() {
  const { forecast, settings } = useApp();
  const rows = forecast.rows;
  const cur = settings.currency;

  const suppliers = useMemo(() => buildSupplierStats(rows), [rows]);

  return (
    <>
      <Topbar title="Supplier Insights" subtitle="Performance scorecards · no ordering, no checkout — analytics only." />
      <div className="flex-1 px-8 py-7 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="supplier-scorecards">
          {suppliers.map((s) => {
            const Icon = TONE_ICON[s.tone];
            return (
              <div key={s.SupplierName} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`supplier-card-${s.SupplierName}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-slate-900 truncate">{s.SupplierName}</div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">
                      <span className="font-mono">{s.skus}</span> SKUs supplied
                    </div>
                  </div>
                  <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center">
                    <Truck className="h-4 w-4 text-slate-600" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Metric label="Inventory Value" value={`${cur}${fmt(s.inventoryValue)}`} />
                  <Metric label="Reorder Value" value={`${cur}${fmt(s.reorderValue)}`} />
                  <Metric label="Stockout-Risk SKUs" value={fmt(s.stockoutRiskSKUs)} tone={s.stockoutRiskSKUs > 0 ? "text-red-700" : ""} />
                  <Metric label="OTD / Fill Rate" value={`${pct(s.otd)} / ${pct(s.fill)}`} />
                </div>

                <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${TONE_CLASS[s.tone]}`}>
                  <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{s.action}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg" data-testid="supplier-table">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="font-heading font-semibold text-slate-900">All Suppliers</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th className="num">SKUs</th>
                  <th className="num">Stockout-Risk</th>
                  <th className="num">Inventory Value</th>
                  <th className="num">Reorder Value</th>
                  <th className="num">OTD %</th>
                  <th className="num">Fill Rate %</th>
                  <th>Suggested Action</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr key={s.SupplierName}>
                    <td className="text-slate-900 font-medium">{s.SupplierName}</td>
                    <td className="num">{fmt(s.skus)}</td>
                    <td className="num">{fmt(s.stockoutRiskSKUs)}</td>
                    <td className="num">{cur}{fmt(s.inventoryValue)}</td>
                    <td className="num">{cur}{fmt(s.reorderValue)}</td>
                    <td className="num">{pct(s.otd)}</td>
                    <td className="num">{pct(s.fill)}</td>
                    <td className="text-slate-600">{s.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function Metric({ label, value, tone = "" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono text-sm font-semibold mt-1 ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}
